/**
 * Wizard de criacao/edicao de Plano de Cobranca.
 *
 * 5 etapas (Quem define o valor, Regras, Parcelas, Multas, Mensagem).
 * Atualmente etapas 1 e 2 sao funcionais; 3-5 estao "Em breve".
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@payflow/ui";
import {
  chargePlansService,
  type ChargePlan,
} from "../../../services/charge-plans.service";
import { Step1Onboarding } from "./steps/Step1Onboarding";
import { Step1Modality as Step2Modality } from "./steps/Step1Modality";
import { Step2Rules as Step3Rules } from "./steps/Step2Rules";
import { Step3Installments as Step4Installments } from "./steps/Step3Installments";
import { Step4Fines as Step5Fines } from "./steps/Step4Fines";
import { Step5Notifications as Step6Notifications } from "./steps/Step5Notifications";
import { Step6Documents as Step7Documents } from "./steps/Step6Documents";
import { Step8Review } from "./steps/Step8Review";
import {
  DEFAULT_DOCUMENTOS,
  DEFAULT_NOTIF_TEXTS,
  DEFAULT_PLAN,
  LAST_STEP,
  makeDefaultOnboardingConfig,
  toVisualStep,
  type PlanFormState,
  type VisualWizardStep,
  type WizardStep,
} from "./types";
import {
  validateAll,
  validateStep,
  type ValidationMessage,
} from "./validation";

export function ChargePlanWizardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();

  const [state, setState] = useState<PlanFormState>(DEFAULT_PLAN);
  const [step, setStep] = useState<WizardStep>(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationMessage[]>(
    [],
  );

  // Carrega plano existente em modo edicao
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    chargePlansService
      .get(id)
      .then((plan) => {
        setState(planToState(plan));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const isEdit = !!id;

  const handleSave = async (
    newStatus?: "rascunho" | "ativo",
    opts?: { redirectToList?: boolean },
  ) => {
    // Publicar exige todos os steps validos.
    if (newStatus === "ativo") {
      const result = validateAll(state);
      if (!result.valid) {
        setValidationErrors(result.errors);
        setError(null);
        // Leva o gestor para a primeira etapa com erro
        const firstFailing = findFirstInvalidStep(state);
        if (firstFailing) setStep(firstFailing);
        return;
      }
    }

    setSaving(true);
    setError(null);
    setValidationErrors([]);
    try {
      const payload = stateToPayload(state, newStatus ?? state.status);
      const result = isEdit
        ? await chargePlansService.update(id!, payload)
        : await chargePlansService.create(payload);

      if (opts?.redirectToList || newStatus === "ativo") {
        navigate("/dashboard/campanhas");
      } else if (!isEdit) {
        navigate(`/dashboard/campanhas/${result.id}`);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    // Avanco entre steps exige que o step atual esteja valido.
    const result = validateStep(step, state);
    if (!result.valid) {
      setValidationErrors(result.errors);
      setError(null);
      // Scroll pro topo onde o banner aparece
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setValidationErrors([]);
    if (step < LAST_STEP) {
      setStep((step + 1) as WizardStep);
      // Ao trocar de etapa, volta pro topo — evita o gestor ter que rolar
      // manualmente pra ver o cabecalho da nova etapa.
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goPrev = () => {
    setValidationErrors([]);
    if (step > 1) {
      setStep((step - 1) as WizardStep);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-12">
      {/* Header com breadcrumb e CTA principais */}
      <header className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard/campanhas")}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted"
            aria-label={t("common.back")}
          >
            ←
          </button>
          <nav className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => navigate("/dashboard/campanhas")}
              className="text-muted-foreground hover:text-foreground"
            >
              {t("campaigns.title")}
            </button>
            <span className="text-muted-foreground">›</span>
            <span className="font-bold text-foreground">
              {state.nome.trim() ||
                (isEdit ? t("wizard.breadcrumb_default_edit") : t("wizard.breadcrumb_default_new"))}
            </span>
            {step >= 2 && (
              <>
                <span className="text-muted-foreground">›</span>
                <span className="font-bold text-foreground">
                  {t(`wizard.steps.${step}`)}
                </span>
              </>
            )}
          </nav>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSave("rascunho")}
            disabled={saving}
          >
            <span className="mr-2">💾</span>
            {t("wizard.save_draft")}
          </Button>
          <Button
            /*
             * Botao "Salvar" do header: salva com o status atual (nao muda
             * pra ativo automaticamente) e leva o gestor de volta pra lista
             * de campanhas. Se for a ultima etapa, mantem o comportamento
             * de publicacao (validacao completa + status=ativo + redirect).
             */
            onClick={() =>
              step === LAST_STEP
                ? handleSave("ativo")
                : handleSave(undefined, { redirectToList: true })
            }
            disabled={saving}
          >
            <span className="mr-2">💾</span>
            {step === LAST_STEP ? t("wizard.save_and_publish") : t("common.save")}
          </Button>
        </div>
      </header>

      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/5 px-4 py-3 text-sm text-error">
          {error}
        </div>
      ) : null}

      {validationErrors.length > 0 ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="mb-1 flex items-center gap-2 font-bold">
            <span>⚠</span>
            <span>
              {validationErrors.length === 1
                ? t("wizard.validation_singular")
                : t("wizard.validation_plural", { count: validationErrors.length })}
            </span>
          </div>
          <ul className="ml-6 list-disc space-y-0.5">
            {validationErrors.map((err, i) => {
              const params: Record<string, string | number> = {
                ...(err.params ?? {}),
              };
              // Traduz labels aninhados (ex: gateLabel/name/type)
              for (const k of ["gateLabel", "name", "type"] as const) {
                const v = params[k];
                if (typeof v === "string" && v.includes(".")) {
                  params[k] = t(v);
                }
              }
              return <li key={i}>{t(err.key, params)}</li>;
            })}
          </ul>
        </div>
      ) : null}

      {/* Indicador visual de 4 etapas (mockup) */}
      <VisualSteps current={toVisualStep(step)} />

      {/* Step content */}
      {step === 1 && <Step1Onboarding state={state} setState={setState} />}
      {step === 2 && <Step2Modality state={state} setState={setState} />}
      {/*
        Ordem invertida: na UI o "Plano de parcelas e prazos" passou a ser o
        passo 2 e "Regras da cobranca" o passo 3. Para nao mexer nos ids dos
        steps (e nas chaves i18n) so trocamos qual componente renderiza em
        cada step. A validacao em validation.ts foi trocada do mesmo jeito.
      */}
      {step === 3 && <Step4Installments state={state} setState={setState} />}
      {step === 4 && <Step3Rules state={state} setState={setState} />}
      {step === 5 && <Step5Fines state={state} setState={setState} />}
      {step === 6 && <Step6Notifications state={state} setState={setState} />}
      {step === 7 && <Step7Documents state={state} setState={setState} />}
      {step === 8 && <Step8Review state={state} />}

      {/* Footer navigation */}
      <footer className="sticky bottom-0 -mx-4 mt-4 flex items-center justify-between gap-3 border-t border-border bg-card/95 px-4 py-3 backdrop-blur lg:-mx-6 lg:px-6">
        <Button variant="outline" onClick={goPrev} disabled={step === 1 || saving}>
          {t("common.back")}
        </Button>
        <Button
          onClick={() => (step === LAST_STEP ? handleSave("ativo") : goNext())}
          disabled={saving}
        >
          {step === LAST_STEP
            ? t("wizard.save_and_publish")
            : t("wizard.continue_next_step")}
        </Button>
      </footer>
    </div>
  );
}

// ====== helpers ======

function stateToPayload(state: PlanFormState, status: PlanFormState["status"]) {
  // Rascunho: aceita nome vazio com placeholder; ativo: nome ja foi validado.
  const nomeTrimmed = state.nome.trim();
  const nomeFinal =
    nomeTrimmed.length > 0
      ? nomeTrimmed
      : `Rascunho ${new Date().toLocaleDateString("pt-BR")} ${new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  return {
    nome: nomeFinal,
    codigo: state.codigo.trim() || undefined!,
    status,
    tipoValor: state.tipoValor,
    // valorFixo e legacy/deprecated apos a inversao da logica: ambos os modos
    // agora usam faixa min/max. Persistimos null para limpar dados antigos.
    valorFixo: null,
    valorMinimo: normalizeMoney(state.valorMinimo),
    valorMaximo: normalizeMoney(state.valorMaximo),
    // valorStep so se aplica no modo "Eu defino" (fixo) com toggle de incremento ON.
    // Caso contrario persiste como null (cliente comeca/fica no minimo, ou escolhe livre).
    valorStep:
      state.tipoValor === "fixo" && state.incrementoHabilitado
        ? normalizeMoney(state.valorStep)
        : null,
    maxActiveCharges: state.maxActiveCharges,

    renovacaoHabilitada: state.renovacaoHabilitada,
    renovacaoIncrementoTipo: state.renovacaoIncrementoTipo,
    renovacaoIncrementoValor: normalizeMoney(state.renovacaoIncrementoValor),
    // Toggle de progresso: persiste apenas o campo do modo selecionado;
    // o outro vai como null para evitar gates obsoletos.
    renovacaoParcelasMinPagas:
      state.renovacaoProgressoTipo === "parcelas"
        ? toInt(state.renovacaoParcelasMinPagas)
        : null,
    renovacaoPercentualPagoMin:
      state.renovacaoProgressoTipo === "percentual"
        ? normalizeMoney(state.renovacaoPercentualPagoMin)
        : null,
    renovacaoDiasMin: toInt(state.renovacaoDiasMin),
    renovacaoMultasMax: toInt(state.renovacaoMultasMax),
    renovacaoMultasAbertasMax: toInt(state.renovacaoMultasAbertasMax),

    diasSemana: state.diasSemana,
    incluirFeriados: state.incluirFeriados,

    tipoMulta: state.tipoMulta,
    multaPercentual: normalizeMoney(state.multaPercentual),
    multaValorFixo: normalizeMoney(state.multaValorFixo),
    multaAplicarSobre: state.multaAplicarSobre || undefined,
    multaExplicacao: state.multaExplicacao || undefined,

    mensagemCliente: state.mensagemCliente || undefined,
    notificacoesConfig: {
      organizacao: state.notificacoes.organizacao,
      parcela: state.notificacoes.parcela,
      ultimo: state.notificacoes.ultimo,
    },
    documentosConfig: {
      documentos: state.documentos,
    },
    validacaoParceiroHabilitada: state.validacaoParceiroHabilitada,
    onboardingConfig: state.onboardingConfig,
    installments:
      state.installments.length > 0
        ? state.installments.map((i) => ({
            tipo: i.tipo,
            label: i.label || undefined,
            taxaPct: normalizeMoney(i.taxaPct) ?? "0",
            numParcelas: i.numParcelas,
            // Piso da faixa (cliente escolhe entre min e max no mobile).
            // Sem enviar aqui, o backend fazia fallback pra num_parcelas
            // (min = max), zerando a faixa configurada pelo gestor.
            numParcelasMin: i.numParcelasMin,
            intervaloDias:
              i.intervaloDias != null && i.intervaloDias > 0
                ? i.intervaloDias
                : undefined,
            ordem: i.ordem,
            ativo: i.ativo,
          }))
        : [
            {
              tipo: "diario" as const,
              taxaPct: "0",
              numParcelas: 20,
              label: "Diario 20x (placeholder)",
            },
          ],
  } as any;
}

function planToState(p: ChargePlan): PlanFormState {
  return {
    nome: p.nome,
    codigo: p.codigo,
    status: p.status,
    tipoValor: p.tipoValor,
    valorFixo: p.valorFixo ?? "",
    valorMinimo: p.valorMinimo ?? "",
    valorMaximo: p.valorMaximo ?? "",
    valorStep: p.valorStep ?? "100",
    incrementoHabilitado: p.valorStep != null && parseFloat(p.valorStep) > 0,
    maxActiveCharges: p.maxActiveCharges ?? 1,
    renovacaoIncrementoTipo: p.renovacaoIncrementoTipo,
    renovacaoIncrementoValor: p.renovacaoIncrementoValor ?? "",
    diasSemana: p.diasSemana,
    incluirFeriados: p.incluirFeriados,
    renovacaoHabilitada: p.renovacaoHabilitada,
    // Deriva o discriminador: se vier % do contrato (nao-null), usa esse modo;
    // caso contrario eh o modo legacy por numero de parcelas.
    renovacaoProgressoTipo:
      p.renovacaoPercentualPagoMin != null ? "percentual" : "parcelas",
    renovacaoParcelasMinPagas: String(p.renovacaoParcelasMinPagas ?? ""),
    renovacaoPercentualPagoMin: p.renovacaoPercentualPagoMin ?? "",
    renovacaoDiasMin: String(p.renovacaoDiasMin ?? ""),
    renovacaoMultasMax: String(p.renovacaoMultasMax ?? ""),
    renovacaoMultasAbertasMax: String(p.renovacaoMultasAbertasMax ?? ""),
    tipoMulta: p.tipoMulta,
    multaPercentual: p.multaPercentual ?? "",
    multaValorFixo: p.multaValorFixo ?? "",
    multaAplicarSobre: p.multaAplicarSobre ?? "sobre_toda_divida",
    multaExplicacao: p.multaExplicacao ?? "",
    installments: p.installments,
    mensagemCliente: p.mensagemCliente ?? "",
    notificacoes: {
      organizacao: {
        ativo: p.notificacoesConfig?.organizacao?.ativo ?? true,
        texto:
          p.notificacoesConfig?.organizacao?.texto ?? DEFAULT_NOTIF_TEXTS.organizacao,
      },
      parcela: {
        ativo: p.notificacoesConfig?.parcela?.ativo ?? true,
        texto:
          p.notificacoesConfig?.parcela?.texto ?? DEFAULT_NOTIF_TEXTS.parcela,
      },
      ultimo: {
        ativo: p.notificacoesConfig?.ultimo?.ativo ?? true,
        texto: p.notificacoesConfig?.ultimo?.texto ?? DEFAULT_NOTIF_TEXTS.ultimo,
      },
    },
    documentos: mergeDocumentos(p.documentosConfig?.documentos),
    validacaoParceiroHabilitada: p.validacaoParceiroHabilitada ?? false,
    // Compatibilidade: planos antigos podem nao ter onboardingConfig persistido.
    // Ofereca o "modelo pronto" como base para o gestor customizar.
    onboardingConfig:
      p.onboardingConfig &&
      typeof p.onboardingConfig === "object" &&
      "dadosPessoais" in (p.onboardingConfig as object)
        ? (p.onboardingConfig as PlanFormState["onboardingConfig"])
        : makeDefaultOnboardingConfig(),
  };
}

function mergeDocumentos(
  saved: NonNullable<ChargePlan["documentosConfig"]>["documentos"] | undefined,
) {
  const base = DEFAULT_DOCUMENTOS.map((d) => ({ ...d }));
  if (!saved) return base;
  for (const raw of saved) {
    // Backward-compat:
    //  - "nota_fiscal" (legacy) -> "ccb" (renomeacao)
    //  - "validacao_parceiro" -> ignorado (tipo descontinuado)
    if ((raw.tipo as string) === "validacao_parceiro") continue;
    const tipo =
      (raw.tipo as string) === "nota_fiscal" ? "ccb" : raw.tipo;
    const idx = base.findIndex((b) => b.tipo === tipo);
    if (idx >= 0) base[idx] = { ...base[idx], ...raw, tipo: tipo as typeof base[number]["tipo"] };
  }
  return base;
}

function toInt(v: string): number | undefined {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Normaliza valores monetarios pt-BR ("1.200,00") para formato decimal
 * aceito pelo Postgres ("1200.00"). Aceita tambem ja-normalizados ("1200").
 */
function normalizeMoney(v: string | undefined | null): string | undefined {
  if (!v) return undefined;
  const trimmed = String(v).trim();
  if (!trimmed) return undefined;
  if (trimmed.includes(",")) {
    return trimmed.replace(/\./g, "").replace(",", ".");
  }
  return trimmed;
}

function findFirstInvalidStep(state: PlanFormState): WizardStep | null {
  for (const step of [1, 2, 3, 4, 5, 6, 7, 8] as WizardStep[]) {
    if (!validateStep(step, state).valid) return step;
  }
  return null;
}

// ====== Visual stepper (4 etapas, mockup) ======

function VisualSteps({ current }: { current: VisualWizardStep }) {
  const { t } = useTranslation();
  const items: { id: VisualWizardStep; title: string; subtitle: string }[] = [
    {
      id: 1,
      title: t("wizard.visual_steps.1.title"),
      subtitle: t("wizard.visual_steps.1.subtitle"),
    },
    {
      id: 2,
      title: t("wizard.visual_steps.2.title"),
      subtitle: t("wizard.visual_steps.2.subtitle"),
    },
    {
      id: 3,
      title: t("wizard.visual_steps.3.title"),
      subtitle: t("wizard.visual_steps.3.subtitle"),
    },
    {
      id: 4,
      title: t("wizard.visual_steps.4.title"),
      subtitle: t("wizard.visual_steps.4.subtitle"),
    },
  ];
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-border bg-card px-5 py-4">
      {items.map((it, idx) => {
        const active = it.id === current;
        const done = it.id < current;
        return (
          <div key={it.id} className="flex flex-1 items-center gap-3 min-w-[180px]">
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold transition ${
                active
                  ? "bg-primary-600 text-white shadow"
                  : done
                  ? "bg-primary-100 text-primary-700"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {done ? "✓" : it.id}
            </span>
            <div className="flex flex-col">
              <span
                className={`text-sm font-bold ${
                  active ? "text-foreground" : "text-muted-foreground"
                }`}
              >
                {it.title}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {it.subtitle}
              </span>
            </div>
            {idx < items.length - 1 ? (
              <span className="hidden h-px flex-1 self-center bg-border md:block" />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
